import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { AuthNonceHeader, ProfileReferrals, type Referrals } from '@/api/spec.ts';
import { environment } from '../../../environment.ts';
import useAppStore from '../../../store/rootStore.ts';
import { getValidNonce } from '../../../store/slices/authSlice.ts';

const EMPTY_REFERRALS: Referrals = {
  at: {},
  t1_referrals: 0,
  t2_referrals: 0,
  total_rewards: '0',
  epoch_rewards: '0',
  total_referral_bonus: '0',
  d: [],
};

const fetchProfileReferrals = async (authNonce: string): Promise<Referrals> => {
  const response = await fetch(`${environment.apiUrl}${ProfileReferrals}`, {
    headers: {
      [AuthNonceHeader]: authNonce,
    },
  });

  if (response.status === 404) {
    return EMPTY_REFERRALS;
  }

  if (!response.ok) {
    throw new Error('Failed to fetch referrals');
  }

  return response.json();
};

/** Per-observer overrides: `select` to narrow what a caller re-renders on, and its share of the poll. */
type ProfileReferralsOptions<T> = Pick<
  UseQueryOptions<Referrals, Error, T>,
  'select' | 'refetchInterval' | 'refetchOnWindowFocus'
>;

export const useProfileReferrals = <T = Referrals>(options?: ProfileReferralsOptions<T>) => {
  const isFullyAuthorized = useAppStore((state) => state.isFullyAuthorized());
  const authNonce = useAppStore((state) => getValidNonce(state.authNonce)?.value);
  const referralsEnabled = useAppStore((state) => state.config?.features?.referralsEnabled);

  return useQuery<Referrals, Error, T>({
    queryKey: ['profile-referrals', authNonce],
    queryFn: () => fetchProfileReferrals(authNonce!),
    enabled: isFullyAuthorized && Boolean(authNonce) && referralsEnabled,
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    ...options,
  });
};
